/**
 * Reads a persisted connector-parameter undo plan back from untrusted storage.
 *
 * This parses, it does not repair. Dropping a malformed entry would leave a
 * checkpoint that still calls itself revertible while silently reverting less
 * than it promises — and that checkpoint is what authorizes a destructive
 * type/instance replacement. A record that cannot be read in full is therefore
 * quarantined as `unusable`: kept so the user is told parameters are applied,
 * stripped so nothing can execute from it.
 */

import {
  countUndoPlanEntries,
  countUndoPlanFamilies,
} from '../lib/parameterUndo/undoPlanCounts';
import {
  markUndoPlanUnusable,
  UnusableUndoPlanMessage,
} from '../lib/parameterUndo/undoPlanExecution';
import type {
  ConnectorAppliedTarget,
  ConnectorParameterRestoreEntry,
  ConnectorParameterUndoEntry,
  ConnectorParameterUndoPlan,
  ConnectorProjectParameterUndoEntry,
} from '../types';

type PlanStatus = ConnectorParameterUndoPlan['status'];

interface PlanHeader {
  id: string;
  createdAt: string;
  projectKey: string;
  familyCount: number;
  entryCount: number;
  status: PlanStatus;
  error?: string;
}

interface ExecutableMaterial {
  projectParameterNames: string[];
  projectEntries: ConnectorProjectParameterUndoEntry[];
  entries: ConnectorParameterUndoEntry[];
  familyParameterRestoreEntries: ConnectorParameterRestoreEntry[];
}

interface UndoPlanSanitizerOptions {
  requireProjectCheckpoint?: boolean;
}

type Provenance =
  | { kind: 'absent' }
  | { kind: 'intact'; target: ConnectorAppliedTarget }
  | { kind: 'damaged' };

const planStatuses = new Set<PlanStatus>([
  'available',
  'undone',
  'undo-failed',
  'unusable',
]);

const isFilledText = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const isCount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0;

const isBindingKind = (value: unknown): value is 'type' | 'instance' =>
  value === 'type' || value === 'instance';

/** Maps every element or gives up: a partial list is a partial promise. */
const parseAll = <T>(
  values: unknown,
  parseOne: (value: unknown) => T | null
): T[] | null => {
  if (values === undefined) {
    return [];
  }

  if (!Array.isArray(values)) {
    return null;
  }

  const parsed: T[] = [];
  for (const value of values) {
    const item = parseOne(value);
    if (!item) {
      return null;
    }

    parsed.push(item);
  }

  return parsed;
};

const readPlanHeader = (candidate: Record<string, unknown>): PlanHeader | null => {
  if (
    !isFilledText(candidate.id) ||
    !isFilledText(candidate.createdAt) ||
    !isFilledText(candidate.projectKey) ||
    !isCount(candidate.familyCount) ||
    !isCount(candidate.entryCount) ||
    !planStatuses.has(candidate.status as PlanStatus) ||
    !Array.isArray(candidate.entries)
  ) {
    return null;
  }

  return {
    id: candidate.id,
    createdAt: candidate.createdAt,
    projectKey: candidate.projectKey,
    familyCount: candidate.familyCount,
    entryCount: candidate.entryCount,
    status: candidate.status as PlanStatus,
    ...(typeof candidate.error === 'string' ? { error: candidate.error } : {}),
  };
};

const parseUndoEntry = (value: unknown): ConnectorParameterUndoEntry | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const entry = value as Record<string, unknown>;
  if (
    !isFilledText(entry.familyId) ||
    !isFilledText(entry.familyName) ||
    !isFilledText(entry.parameterName) ||
    typeof entry.existedBefore !== 'boolean' ||
    (entry.previousBindingKind !== undefined && !isBindingKind(entry.previousBindingKind))
  ) {
    return null;
  }

  return {
    familyId: entry.familyId,
    familyName: entry.familyName,
    parameterName: entry.parameterName,
    existedBefore: entry.existedBefore,
    ...(isBindingKind(entry.previousBindingKind)
      ? { previousBindingKind: entry.previousBindingKind }
      : {}),
  };
};

const parseProjectUndoEntry = (
  value: unknown,
): ConnectorProjectParameterUndoEntry | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const entry = value as Record<string, unknown>;
  const previousCategoryIds = parseAll(entry.previousCategoryIds, (categoryId) =>
    typeof categoryId === 'string' && /^-?\d+$/.test(categoryId)
      ? categoryId
      : null
  );
  if (
    !isFilledText(entry.parameterName) ||
    typeof entry.existedBefore !== 'boolean' ||
    !previousCategoryIds ||
    (entry.previousBindingKind !== undefined && !isBindingKind(entry.previousBindingKind)) ||
    (entry.previousGroupTypeId !== undefined && !isFilledText(entry.previousGroupTypeId)) ||
    (entry.existedBefore && (
      !isBindingKind(entry.previousBindingKind) ||
      previousCategoryIds.length === 0 ||
      !isFilledText(entry.previousGroupTypeId)
    ))
  ) {
    return null;
  }

  return {
    parameterName: entry.parameterName,
    existedBefore: entry.existedBefore,
    ...(isBindingKind(entry.previousBindingKind)
      ? { previousBindingKind: entry.previousBindingKind }
      : {}),
    previousCategoryIds,
    ...(isFilledText(entry.previousGroupTypeId)
      ? { previousGroupTypeId: entry.previousGroupTypeId }
      : {}),
  };
};

const parseRestoreEntry = (value: unknown): ConnectorParameterRestoreEntry | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const entry = value as Record<string, unknown>;
  if (
    !isFilledText(entry.familyId) ||
    !isFilledText(entry.familyName) ||
    !isFilledText(entry.parameterName) ||
    !isBindingKind(entry.bindingKind) ||
    (entry.targetObjectType !== undefined && typeof entry.targetObjectType !== 'string')
  ) {
    return null;
  }

  return {
    familyId: entry.familyId,
    familyName: entry.familyName,
    parameterName: entry.parameterName,
    bindingKind: entry.bindingKind,
    ...(typeof entry.targetObjectType === 'string'
      ? { targetObjectType: entry.targetObjectType }
      : {}),
  };
};

// The same counting rule the producers write with, so a plan that was built
// honestly always reads back as executable.
const agreesWithHeader = (material: ExecutableMaterial, header: PlanHeader): boolean =>
  header.entryCount === countUndoPlanEntries(material) &&
  header.familyCount === countUndoPlanFamilies(material);

const holdsWork = (material: ExecutableMaterial): boolean =>
  material.projectParameterNames.length > 0 ||
  material.projectEntries.length > 0 ||
  material.entries.length > 0 ||
  material.familyParameterRestoreEntries.length > 0;

/**
 * Returns the undo material only when all of it reads back and the stored
 * counts match it exactly; otherwise the record claims work it cannot prove.
 */
const readExecutableMaterial = (
  candidate: Record<string, unknown>,
  header: PlanHeader,
  requireProjectCheckpoint: boolean,
): ExecutableMaterial | null => {
  const projectParameterNames = parseAll(candidate.projectParameterNames, (value) =>
    isFilledText(value) ? value : null
  );
  const projectEntries = parseAll(candidate.projectEntries, parseProjectUndoEntry);
  const entries = parseAll(candidate.entries, parseUndoEntry);
  const familyParameterRestoreEntries = parseAll(
    candidate.familyParameterRestoreEntries,
    parseRestoreEntry
  );

  if (!projectParameterNames || !projectEntries || !entries || !familyParameterRestoreEntries) {
    return null;
  }

  if (requireProjectCheckpoint && projectParameterNames.length > 0 && projectEntries.length === 0) {
    return null;
  }

  const checkpointNames = new Set(
    projectEntries.map((entry) => entry.parameterName.trim().toLowerCase()),
  );
  const advertisedNames = new Set(
    projectParameterNames.map((name) => name.trim().toLowerCase()),
  );
  if (projectEntries.length > 0 && (
    checkpointNames.size !== advertisedNames.size ||
    [...checkpointNames].some((name) => !advertisedNames.has(name))
  )) {
    return null;
  }

  const material = {
    projectParameterNames,
    projectEntries,
    entries,
    familyParameterRestoreEntries,
  };
  return agreesWithHeader(material, header) && holdsWork(material) ? material : null;
};

/**
 * Provenance is only worth keeping when every field is intact and belongs to
 * this plan's project: a partially readable applied target would let a
 * replacement run against a guess.
 */
const readProvenance = (candidate: Record<string, unknown>, projectKey: string): Provenance => {
  const value = candidate.appliedTarget;
  if (value === undefined) {
    return { kind: 'absent' };
  }

  if (!value || typeof value !== 'object') {
    return { kind: 'damaged' };
  }

  const target = value as Record<string, unknown>;
  const isIntact =
    (target.destination === 'project' || target.destination === 'family') &&
    isBindingKind(target.bindingKind) &&
    target.projectKey === projectKey &&
    isFilledText(target.documentTitle) &&
    isFilledText(target.documentPath) &&
    isFilledText(target.setupFingerprint) &&
    isCount(target.setupRevisionId);

  return isIntact
    ? { kind: 'intact', target: target as unknown as ConnectorAppliedTarget }
    : { kind: 'damaged' };
};

const withProvenance = (
  plan: ConnectorParameterUndoPlan,
  provenance: Provenance
): ConnectorParameterUndoPlan =>
  provenance.kind === 'intact' ? { ...plan, appliedTarget: provenance.target } : plan;

export const sanitizeConnectorParameterUndoPlan = (
  value: unknown,
  options: UndoPlanSanitizerOptions = {},
): ConnectorParameterUndoPlan | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const candidate = value as Record<string, unknown>;
  const header = readPlanHeader(candidate);
  if (!header) {
    return null;
  }

  const provenance = readProvenance(candidate, header.projectKey);
  const emptyPlan: ConnectorParameterUndoPlan = {
    ...header,
    projectParameterNames: [],
    projectEntries: [],
    entries: [],
  };

  // A reverted plan holds nothing left to execute, so damage in it raises no
  // alarm — it stays reported as reverted.
  if (header.status === 'undone') {
    return withProvenance(emptyPlan, provenance);
  }

  const material = readExecutableMaterial(
    candidate,
    header,
    options.requireProjectCheckpoint ?? true,
  );
  if (!material || provenance.kind === 'damaged') {
    return markUndoPlanUnusable(
      withProvenance(emptyPlan, provenance),
      UnusableUndoPlanMessage
    );
  }

  return withProvenance(
    {
      ...emptyPlan,
      projectParameterNames: material.projectParameterNames,
      ...(material.projectEntries.length > 0
        ? { projectEntries: material.projectEntries }
        : {}),
      entries: material.entries,
      ...(material.familyParameterRestoreEntries.length > 0
        ? { familyParameterRestoreEntries: material.familyParameterRestoreEntries }
        : {}),
    },
    provenance
  );
};
